import PropTypes from 'prop-types';
import { useContext } from 'react';
import { NavLink } from 'react-router-dom';

import BreadcrumbsContext from './BreadcrumbsContext';
import BreadcrumbsDropdown from './BreadcrumbsDropdown';

import './styles.css';

/**
 * @param {import("./BreadcrumbsContext").Crumb} a
 * @param {import("./BreadcrumbsContext").Crumb} b
 */
const bySlicedUriLength = (a, b) =>
  a.uri.split('/').length - b.uri.split('/').length;

/**
 *
 * @param {import("./BreadcrumbsContext").Crumb} crumb
 * @param {number} index
 * @param {React.ReactNode<'li'>[]} arr
 */
const toListItem = (crumb) => {
  const activeClasses = 'font-semibold text-gray-4 pointer-events-none';

  return (
    <li
      className="breadcrumb-item inline-block after:mx-2 after:my-0"
      key={`breadcrumb-${crumb.label}-${crumb.uri}`}
    >
      <NavLink
        className={({ isActive }) => (isActive ? activeClasses : null)}
        end
        to={crumb.uri}
      >
        {crumb.label}
      </NavLink>
    </li>
  );
};

const BREADCRUMB_DROPDOWN_THRESHHOLD = 4;
const STARTING_CAP_DROPDOWN_OFFSET = 1;

/**
 *
 * @typedef {{
 *  ariaLabel: string,
 *  crumbsDropdownAriaLabel: string,
 * }} Props
 *
 * @type {React.FC<Props>} Breadcrumbs
 */
const Breadcrumbs = ({ ariaLabel, crumbsDropdownAriaLabel, ...rest }) => {
  const { crumbs, deferredCrumbIds } = useContext(BreadcrumbsContext);
  const sortedCrumbs = [...crumbs].sort(bySlicedUriLength);

  if (sortedCrumbs.length <= BREADCRUMB_DROPDOWN_THRESHHOLD) {
    const navCrumbs = sortedCrumbs.map(toListItem);

    return (
      <nav aria-label={ariaLabel} aria-live="polite">
        <ol
          className={`pt-4 pb-6 pl-0 m-0 text-sm ${
            deferredCrumbIds.length > 0 ? 'invisible' : ''
          }`}
          {...rest}
        >
          {navCrumbs}
        </ol>
      </nav>
    );
  }

  const dropdownCrumbs = sortedCrumbs.splice(
    STARTING_CAP_DROPDOWN_OFFSET,
    sortedCrumbs.length - 3,
  );

  const navCrumbs = sortedCrumbs.map(toListItem);
  navCrumbs.splice(
    STARTING_CAP_DROPDOWN_OFFSET,
    0,
    <BreadcrumbsDropdown
      ariaLabel={crumbsDropdownAriaLabel}
      crumbs={dropdownCrumbs}
      key="breadcrumb-dropdown"
    />,
  );

  return (
    <nav aria-label={ariaLabel} aria-live="polite">
      <ol
        className={`pt-4 pb-6 pl-0 m-0 text-sm ${
          deferredCrumbIds.length > 0 ? 'invisible' : ''
        }`}
        {...rest}
      >
        {navCrumbs}
      </ol>
    </nav>
  );
};

Breadcrumbs.defaultProps = {
  crumbsDropdownAriaLabel: 'Breadcrumbs Dropdown Button',
};

Breadcrumbs.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  crumbsDropdownAriaLabel: PropTypes.string,
};

export default Breadcrumbs;
